import clsx from 'clsx';
import { forwardRef } from 'react';

import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import { Divider } from '../../layouts/divider/Divider.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { Heading } from '../../typography/heading/Heading.js';
import { getHeadingVariant } from './empty-state-utils.js';
import { contentContainerCss, titleSlotCss } from './EmptyState.sty.js';
import { EmptyStateWrapper } from './EmptyStateWrapper.js';
import { Actions } from './slot-components/Actions.js';
import { Details } from './slot-components/Details.js';
import { Footer } from './slot-components/Footer.js';
import { Title } from './slot-components/Title.js';
import { Visual } from './slot-components/Visual.js';
import { VisualPreset } from './slot-components/VisualPreset.js';
import type { EmptyStateProps } from './types/empty-state-types.js';

const COMPONENT_NAME = 'EmptyState';

const BaseEmptyState = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  EmptyStateProps
>((props, forwardedRef) => {
  const {
    size = 'default',
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  const [slots] = useSlots(children, {
    Actions: Actions,
    Details: Details,
    Footer: Footer,
    Title: Title,
    Visual: Visual,
    VisualPreset: Visual,
  });
  const visualSlot = slots.Visual ? slots.Visual : null;
  const detailsSlot = slots.Details ? slots.Details : null;
  const actionsSlot = slots.Actions ? slots.Actions : null;
  const footerSlot = slots.Footer ? slots.Footer : null;
  const titleSlot = slots.Title ? (
    <Heading
      {...getHeadingVariant(size, slots.Title.props.as)}
      className={clsx(titleSlotCss)}
    >
      {slots.Title}
    </Heading>
  ) : null;

  const hasDivider =
    (slots.Actions || slots.Details || slots.Title || slots.Visual) &&
    slots.Footer;

  const divider = hasDivider ? <Divider aria-hidden /> : null;

  return (
    <EmptyStateWrapper
      size={size}
      ref={forwardedRef}
      className={consumerClassName}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    >
      {visualSlot}
      <Flex
        className={clsx(contentContainerCss)}
        maxWidth={600}
        flexDirection="column"
        gap={0}
      >
        {titleSlot}
        {detailsSlot}
        {actionsSlot}
        {divider}
        {footerSlot}
      </Flex>
    </EmptyStateWrapper>
  );
});

(
  BaseEmptyState as typeof BaseEmptyState & { displayName: string }
).displayName = COMPONENT_NAME;

/**
 * Use the `EmptyState` to communicate that no data is available for a section or view and guide users toward resolving the situation with a clear title, description, and actions.
 * @public
 */
export const EmptyState = Object.assign(BaseEmptyState, {
  Actions,
  Details,
  Footer,
  Title,
  Visual,
  VisualPreset,
});
